/**
 * readingLevel.js — Cycle 56 · Part 1 (Fading audio support)
 * ────────────────────────────────────────────────────────────
 * Reading support level per child (L0/L1/L2/L3). Stored under the
 * child's local record + a mirror in localStorage for zero-cost reads.
 *
 * Levels
 *   L0 FULL         — auto-read every question / instruction / lesson body
 *   L1 GUIDED       — auto-read questions & instructions; body on button
 *   L2 ON-REQUEST   — no auto-read; persistent "read to me" button
 *   L3 INDEPENDENT  — no narration surfaced; tap-a-word only
 *
 * Level is DERIVED by default (from tier + reading signals) but can be
 * PINNED by a parent/teacher. A pinned level is never auto-changed.
 */
import { getActiveChildId, getActiveChild, updateChild } from "@/lib/children";

export const LEVELS = ["L0", "L1", "L2", "L3"];
export const LEVEL_LABELS = {
  L0: "Full audio",
  L1: "Guided (auto-read questions)",
  L2: "On-request (read to me button)",
  L3: "Independent",
};

const LS_LEVEL = "we_reading_level_v1";
const LS_PINNED = "we_reading_level_pinned_v1";

// Legacy read mode toggle from tapToHear — treat "on" as L0/L1 signal.
const LS_READ_MODE = "we_read_mode_v1";

const safeGet = (k) => { try { return localStorage.getItem(k); } catch { return null; } };
const safeSet = (k, v) => { try { localStorage.setItem(k, v); } catch { /* ignore */ } };

const TIER_DEFAULT = {
  seedlings: "L0",
  explorers: "L2",
  pioneers:  "L3",
};

/** Get current child's reading level. Falls back to tier default. */
export function getReadingLevel() {
  const explicit = safeGet(LS_LEVEL);
  if (LEVELS.includes(explicit)) return explicit;
  // Legacy: if parent turned on "Read Mode" but no level saved, treat as L0.
  if (safeGet(LS_READ_MODE) === "1") return "L0";
  const child = getActiveChild();
  return TIER_DEFAULT[child?.tier] || "L2";
}

/** True if this level is pinned (parent set). */
export function isPinned() {
  return safeGet(LS_PINNED) === "1";
}

/** Set the level. `pinned=true` means it survives auto-progression. */
export function setReadingLevel(level, { pinned = false } = {}) {
  if (!LEVELS.includes(level)) return;
  safeSet(LS_LEVEL, level);
  if (pinned) safeSet(LS_PINNED, "1");
  else { try { localStorage.removeItem(LS_PINNED); } catch { /* ignore */ } }
  // Persist onto child profile for cross-device sync via access-code snapshot.
  const child = getActiveChild();
  if (child) updateChild(child.id, { readingLevel: level, readingLevelPinned: !!pinned });
  try {
    window.dispatchEvent(new CustomEvent("we-reading-level-changed", { detail: { level, pinned } }));
  } catch { /* ignore */ }
}

/** Should we auto-read on mount for this content-kind at the current level? */
export function shouldAutoRead(kind) {
  // kind: "question" | "instruction" | "body" | "menu"
  const lv = getReadingLevel();
  if (lv === "L0") return true;
  if (lv === "L1") return kind === "question" || kind === "instruction";
  return false;
}

/** Should we show a persistent "read to me" affordance? */
export function shouldShowReadButton() {
  const lv = getReadingLevel();
  return lv !== "L3"; // Only fully independent hides it.
}

/** Signal recording for auto-progression (used by session-end analytics). */
export function recordReadingSignal(signal) {
  // signal: { kind: "tap_word" | "read_aloud_used" | "phonics_skill_mastered", weight }
  try {
    const key = "we_reading_signals_v1";
    const raw = safeGet(key) || "[]";
    const list = JSON.parse(raw);
    list.push({ ...signal, at: Date.now() });
    // Keep the last 200 signals only.
    const trimmed = list.slice(-200);
    safeSet(key, JSON.stringify(trimmed));
  } catch { /* ignore */ }
}

/**
 * Auto-progression evaluator. Called on session start.
 * Rules: promote/demote by ONE level after sustained (~2 week) signal.
 *  - Promotion signal: falling tap-word rate + rising phonics mastery
 *  - Demotion signal: rising tap-word rate + wrong answers on read-aloud tests
 * Never runs when pinned.
 * Never announces demotion — just changes silently.
 */
export function maybeAutoAdjust() {
  if (isPinned()) return null;
  const current = getReadingLevel();
  const idx = LEVELS.indexOf(current);
  try {
    const signals = JSON.parse(safeGet("we_reading_signals_v1") || "[]");
    const now = Date.now();
    const cutoff = now - 14 * 24 * 60 * 60 * 1000;
    const recent = signals.filter((s) => s.at >= cutoff);
    if (recent.length < 20) return null; // not enough data yet

    const tapCount     = recent.filter((s) => s.kind === "tap_word").length;
    const readAloudUse = recent.filter((s) => s.kind === "read_aloud_used").length;
    const masteredPhon = recent.filter((s) => s.kind === "phonics_skill_mastered").length;

    // Promotion: if tap-word ratio is low + phonics mastery advancing.
    const promote = tapCount / recent.length < 0.15 && masteredPhon >= 3 && idx < LEVELS.length - 1;
    // Demotion: if tap-word ratio is very high + read-aloud button frequently used.
    const demote  = tapCount / recent.length > 0.35 && readAloudUse >= 5 && idx > 0;

    if (promote) {
      setReadingLevel(LEVELS[idx + 1]);
      return { from: current, to: LEVELS[idx + 1], reason: "promote" };
    }
    if (demote) {
      setReadingLevel(LEVELS[idx - 1]);
      return { from: current, to: LEVELS[idx - 1], reason: "demote" };
    }
  } catch { /* ignore */ }
  return null;
}

/** Hydrate the reading level from the current child's profile on switch. */
export function hydrateFromChild() {
  const child = getActiveChild();
  if (!child) return;
  if (child.readingLevel && LEVELS.includes(child.readingLevel)) {
    safeSet(LS_LEVEL, child.readingLevel);
  }
  if (child.readingLevelPinned) safeSet(LS_PINNED, "1");
}
